/* eslint-disable react-hooks/exhaustive-deps */
import styles from "./Compose.module.css"
import {ReactComponent as AttachmentIcon} from "./assets/attachment.svg"
import {ReactComponent as FolderIcon} from "./assets/folder.svg"
import {ReactComponent as EmojiIcon} from "./assets/emoji.svg"
import {ReactComponent as EditIcon} from "./assets/edit.svg"
import {ReactComponent as CloseIcon} from "./assets/close.svg"
import {ReactComponent as FileIcon} from "./assets/file.svg"
import {BaseButton, BaseLoading, Icon} from "components"
import {Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react"
import {observer} from "mobx-react"
import cx from "classnames"
import {Checkbox, Tooltip} from "antd"
import inputStyles from "./Input.module.css"
import {MentionsInput, Mention} from "react-mentions"
import {handleErrorChat, serializeTemplate, toastError} from "helpers"
import {UserAvatar} from "../UserAvatar"
import {CommunicationContext} from "context/CommunicationContext"
import {Communication} from "types/communication"
import {ChannelSelect} from "../ChannelSelect"
import {ReplyMessage} from "../ReplyMessage"
import {useModel, useVisible} from "hooks"
import {MediaLibraryPopup, MessageTemplatePopup, PollPopupV2, TemplateSelection} from "./parts"
import {ChatRoomContext} from "context/ChatRoomContext"
import {chatServiceV2, notificationService, userService, userServiceV3} from "services"
import {uploadUsingPreSignedUrl} from "helpers/chat"
import ChatWsV2 from "sections/NewCommunication/chatWsV2"
import {Descendant} from "slate"
import {INITIAL_RTE_CONTENT, isEmptyContent} from "components/RichTextEditor/slate-custom-types"
import RichTextEditor from "components/RichTextEditor"
import {useThrottle} from "sections/NewCommunication/hooks"
import {BaseDatePicker, BaseTimePicker} from "components/DateTimePicker"
import moment from "moment"
import momentTz from "moment-timezone"
import {MEDIA_LIBRARY_ICONS} from "components/MediaLibrarySelect/MediaLibrarySelect"
import {EmojiPicker} from "../EmojiPicker"
import {v4 as uuidv4} from "uuid"

type Props = {
    editMessage?: Communication.Message
    replyMessage?: Communication.ReplyMessage
    onlyContent?: boolean
    userList?: Communication.RoomUser[]
    forThread?: Communication.Message
    onSuccess?: (message?: Communication.Message | Communication.SupportRoom) => void
    emojiPickerClassName?: string
    onCancelEdit?: () => void
    isPollEnabled?: boolean
    pollRoomId?: number
    isNewSupportRequest?: boolean
}

export const Compose = observer((props: Props) => {
    const {
        editMessage: initialEditMessage,
        replyMessage: initialReplyMessage,
        onlyContent = false,
        userList,
        forThread,
        onSuccess,
        emojiPickerClassName,
        onCancelEdit,
        isPollEnabled,
        pollRoomId,
        isNewSupportRequest
    } = props
    const [msgText, setMsgText] = useState<string>("")
    const [msgTitle, setMsgTitle] = useState<string>("")
    const [sendWithEmail, setSendWithEmail] = useState<boolean>(false)
    const [sendWithSms, setSendWithSms] = useState<boolean>(false)
    const [withActivity, setWithActivity] = useState<boolean>(false)
    const [withStaffsNotification, setWithStaffsNotification] = useState<boolean>(false)
    const [isSending, setIsSending] = useState(false)
    const [replyMessage, setReplyMessage] = useState<Communication.ReplyMessage | undefined>()
    const [editMessage, setEditMessage] = useState<Communication.Message | undefined>()
    const [attachedFiles, setAttachedFiles] = useState<Communication.AttachedFile[]>([])
    const attachFileRef = useRef(null)
    const pollPopup = useVisible(false)
    const emojiPopup = useVisible(false)
    const libraryPopup = useVisible(false)
    const messageTemplatePopup = useVisible(false)
    const [mediaLibraryItems, setMediaLibraryItems] = useState<any[]>([])
    const {activeRoom, activeGroup, moveRoomToFirst} = useContext(CommunicationContext)
    const {activePageId} = useContext(ChatRoomContext)
    const [scheduledChannel, setScheduledChannel] = useState<Communication.ChannelRoom>()

    const model = useModel()
    const isStaffOrAdmin = model.isStaffOrAdmin()
    const userDateFormat = model.getUserDateFormat()
    const userTimeFormat = model.getUserTimeFormat()
    const [canCreateActivity, setCanCreateActivity] = useState<boolean>(false)
    const [selectedTemplate, setSelectedTemplate] = useState(null)
    const [descendants, setDescendants] = useState<Descendant[]>(INITIAL_RTE_CONTENT)
    const [subject, setSubject] = useState<string>("")
    const [scheduledDate, setScheduledDate] = useState<any>()
    const [scheduledTime, setScheduledTime] = useState<any>()

    const throttleText = useThrottle(msgText, 3000)
    const chatSettings = model.chatOptions[Communication.ChatOptions.Settings] as Communication.ChatSettings

    useEffect(() => {
        resetForm()
    }, [activeRoom.roomId, activePageId])

    useEffect(() => {
        checkCreateActivity()
    }, [activeRoom.roomId, activeGroup?.id])

    const isScheduledRoom = useMemo(() => {
        return activeRoom.type === Communication.RoomTypes.Scheduled
    }, [activeRoom.type])

    const isDepartmentRoom = useMemo(() => {
        return [Communication.RoomTypes.Department, Communication.RoomTypes.MultiDepartmental].includes(activeRoom.type)
    }, [activeRoom.type])

    const canSendSms = useMemo(() => {
        if (isDepartmentRoom) {
            const departmentRoom = activeRoom as Communication.DepartmentRoom
            const departmentId = departmentRoom.departmentId ?? 0
            return !!model.smsDepartments.find((id) => id === departmentId)
        } else {
            return model.canSendDirectSms
        }
    }, [isDepartmentRoom, activeRoom, model.smsDepartments, model.canSendDirectSms])

    useEffect(() => {
        if (canSendSms) {
            if (chatSettings?.autoCheckSmsDirectChat && activeRoom.type === Communication.RoomTypes.DirectChat) {
                setSendWithSms(true)
            }
            if (chatSettings?.autoCheckSmsDepartmentalChat && activeRoom.type === Communication.RoomTypes.Department) {
                setSendWithSms(true)
            }
        }
    }, [canSendSms, chatSettings, activeRoom.type])

    const isChannelOrScheduledRoom = useMemo(() => {
        return [Communication.RoomTypes.Channel, Communication.RoomTypes.Scheduled].includes(activeRoom.type)
    }, [activeRoom.type])

    const isDirectOrDepartmentRoom = useMemo(() => {
        return [Communication.RoomTypes.DirectChat, Communication.RoomTypes.Department].includes(activeRoom.type)
    }, [activeRoom.type])

    const isSupportRoom = useMemo(() => {
        return [Communication.RoomTypes.OpenedSupportHub, Communication.RoomTypes.ClosedSupportHub].includes(
            activeRoom.type
        )
    }, [activeRoom.type])

    const serializeWithoutUser = useMemo(
        () =>
            [Communication.RoomTypes.DirectMessage, Communication.RoomTypes.MultiDepartmental].includes(
                activeRoom.type
            ),
        [activeRoom.type]
    )

    const showSendOptions = useMemo(() => {
        return (
            isStaffOrAdmin &&
            ([
                Communication.RoomTypes.Department,
                Communication.RoomTypes.DirectMessage,
                Communication.RoomTypes.MultiDepartmental
            ].includes(activeRoom.type) ||
                (activeRoom.type === Communication.RoomTypes.DirectChat && !activeRoom.isGroup))
        )
    }, [isStaffOrAdmin, activeRoom.type, activeRoom.isGroup])

    useEffect(() => {
        if (initialEditMessage) {
            setEditMessage(initialEditMessage)
            setMsgText(initialEditMessage.content || "")
            setMsgTitle(initialEditMessage.title || "")
            if (initialEditMessage.replyTo) {
                setReplyMessage(initialEditMessage.replyTo)
            }
            if (isScheduledRoom) {
                setScheduledChannel(initialEditMessage.room)
                setScheduledDate(moment(initialEditMessage.scheduledAt))
                setScheduledTime(moment(initialEditMessage.scheduledAt))
            }

            setSendWithEmail(false)
            setSendWithSms(false)
        }
    }, [initialEditMessage, isScheduledRoom])

    useEffect(() => {
        if (initialReplyMessage) {
            setReplyMessage(initialReplyMessage)
            setSendWithEmail(false)
            setSendWithSms(false)
        }
    }, [initialReplyMessage])

    useEffect(() => {
        ;(async () => {
            const isTyping = !!throttleText
            if (isTyping && isDirectOrDepartmentRoom) {
                ChatWsV2.sendPacket({command: Communication.SocketCommand.Typing, payload: {roomId: activeRoom.roomId}})
            }
        })()
    }, [throttleText, activeRoom.roomId, isDirectOrDepartmentRoom])

    const resetForm = useCallback(() => {
        setSendWithEmail(false)
        setSendWithSms(false)
        setWithActivity(false)
        setIsSending(false)
        setReplyMessage(undefined)
        setEditMessage(undefined)
        setAttachedFiles([])
        setMediaLibraryItems([])
        setSubject("")
        setSelectedTemplate(null)
        setMsgText("")
        setMsgTitle("")

        setDescendants(INITIAL_RTE_CONTENT)

        // scheduled
        setScheduledChannel(null)
        setScheduledDate(undefined)
        setScheduledTime(undefined)
    }, [])

    const onToggleSendAsEmail = () => {
        setSendWithEmail((prev) => {
            handleMessageContentChange(!sendWithSms && !prev, prev)

            setReplyMessage(undefined)
            setEditMessage(undefined)
            setAttachedFiles([])
            setMediaLibraryItems([])
            return !prev
        })
    }

    const onToggleSendAsSms = () => {
        setSendWithSms((prev) => {
            handleMessageContentChange(!sendWithEmail && !prev, prev)

            setReplyMessage(undefined)
            setEditMessage(undefined)
            setAttachedFiles([])
            setMediaLibraryItems([])
            return !prev
        })
    }

    const handleMessageContentChange = (convertToHtmlText: boolean, clearText: boolean) => {
        if (convertToHtmlText) {
            const _descendants: Descendant[] = [
                {
                    type: "paragraph",
                    children: [{text: msgText}]
                }
            ]
            setDescendants(_descendants)
        } else {
            if (clearText) {
                setMsgText("")
            }
        }
    }

    const checkCreateActivity = async () => {
        if (isDepartmentRoom) {
            const departmentId = activeGroup?.id ?? activeRoom["departmentId"] ?? 0
            const chatActivityId = model.departmentsChats.find(
                (d) => d.departmentId === departmentId && d.chatActivityId
            )
            setCanCreateActivity(!!chatActivityId)
        } else {
            setCanCreateActivity(false)
        }
    }

    const selectedUserId = useMemo(() => {
        if (
            [Communication.RoomTypes.DirectMessage, Communication.RoomTypes.MultiDepartmental].includes(activeRoom.type)
        ) {
            return null
        }
        let users = (userList || []).filter((x) => !x.isMe)
        if (activeRoom.type === Communication.RoomTypes.Department) {
            users = users.filter((x) => x.isDepartmentChatUser)
        }
        return users.length ? users[0].userId : null
    }, [userList, activeRoom])

    const isContentAvailable = useMemo(() => {
        const textAvailable =
            isChannelOrScheduledRoom && !forThread ? msgTitle && msgTitle !== "" : msgText && msgText !== ""
        return textAvailable || attachedFiles.length > 0 || mediaLibraryItems.length > 0
    }, [msgText, msgTitle, attachedFiles, mediaLibraryItems, isChannelOrScheduledRoom, forThread])

    const isSendEmailAvailable = useMemo(() => {
        if (sendWithEmail) return !!subject && subject !== "" && !isEmptyContent(descendants)
        return true
    }, [sendWithEmail, descendants, subject])

    const isSendSmsAvailable = useMemo(() => {
        if (sendWithSms) return !isEmptyContent(descendants)
        return true
    }, [sendWithSms, descendants])

    const isEditing = useMemo(() => {
        return !!editMessage
    }, [editMessage])

    const isScheduleAvailable = useMemo(() => {
        if (isScheduledRoom) {
            if (scheduledChannel && scheduledDate && scheduledTime) {
                const _date = moment(
                    `${moment(scheduledDate).format("MM/DD/YYYY")} ${moment(scheduledTime).format("HH:mm")}`
                )
                return _date.isAfter(moment())
            }
            return false
        }
        return true
    }, [isScheduledRoom, scheduledChannel, scheduledDate, scheduledTime])

    const isNewSupportAvailable = useMemo(() => {
        if (isNewSupportRequest) {
            return isContentAvailable && !!msgTitle
        }
        return true
    }, [msgTitle, isNewSupportRequest, isContentAvailable])

    const isSendAvailable = useMemo(() => {
        return (
            isContentAvailable &&
            isSendEmailAvailable &&
            isSendSmsAvailable &&
            isScheduleAvailable &&
            isNewSupportAvailable
        )
    }, [isContentAvailable, isScheduleAvailable, isSendEmailAvailable, isSendSmsAvailable, isNewSupportAvailable])

    const onPressEnterSend = (event) => {
        if (event.keyCode === 13 && !event.shiftKey && !isChannelOrScheduledRoom) {
            const isEnterWillSendMessage = chatSettings
                ? chatSettings.returnKey === Communication.ReturnKey.NEW_MESSAGE
                : true
            if (isEnterWillSendMessage) {
                event.preventDefault()
                verifyBeforeSend()
            }
        }
    }

    const verifyBeforeSend = async () => {
        if (!isSending && isSendAvailable) {
            setIsSending(true)
            let _msgText = msgText
            if (sendWithEmail || sendWithSms) {
                let userData = {
                    userDetails: null,
                    profile: null,
                    campusDetails: null,
                    admissionAdvisor: null,
                    finAidAdvisor: null,
                    academicAdvisor: null,
                    studentLedgerStats: null,
                    studentOverallStats: null
                }
                if (!serializeWithoutUser && selectedUserId) {
                    userData = await userServiceV3.getUserDetailsForChat({userId: selectedUserId})
                }
                let text: string = ""
                descendants?.forEach(
                    (descendant) =>
                        (text += serializeTemplate(descendant, userData, {
                            selectedTemplate,
                            userDateFormat,
                            userTimeFormat,
                            serializeWithoutUser
                        }))
                )
                _msgText = text
            }
            if (isChannelOrScheduledRoom && !forThread) {
                if (isScheduledRoom && !scheduledChannel) {
                    toastError("Please select channel to post")
                    return
                }
                onSubmitThread(_msgText)
                return
            }
            if (activeRoom.roomId === Communication.PredefinedRoomId.WithoutDepartmentalRoom) {
                onSendAndCreateDepartmentalMessage(_msgText)
                return
            }
            if (activeRoom.roomId === Communication.PredefinedRoomId.DirectMessage) {
                onSendDirectMessage(_msgText)
                return
            }
            if (activeRoom.type === Communication.RoomTypes.MultiDepartmental) {
                onSendMultiDepartmentalMessage(_msgText)
                return
            }
            if (activeRoom.type === Communication.RoomTypes.StudentTopic) {
                onSubmitStudentNote(_msgText)
                return
            }
            if (isNewSupportRequest) {
                if (!msgTitle) {
                    toastError("Title is required")
                    return
                }
                onSubmitSupport()
                return
            }
            onSendMessage(_msgText)
        }
    }

    const onClickSend = () => {
        verifyBeforeSend()
    }

    const buildAttachedFilesPayload = async () => {
        let _attachedFiles: any[] = [],
            mediaLibraryIds: any[] = []

        const filesToUpload = attachedFiles
            .filter((file) => file.file)
            .map((file) => {
                const fileExtension = file.file.name.split(".").pop()
                return {
                    ...file,
                    fileExtension
                }
            })

        if (filesToUpload.length > 0) {
            const {data: signedUrls} = await chatServiceV2.getPreSignedUrls({
                files: filesToUpload.map((file) => {
                    return {
                        fileExtension: file.fileExtension,
                        id: file.fileId
                    }
                })
            })

            filesToUpload.forEach((file) => {
                const signedUrl = signedUrls.find((url) => url.id === file.fileId)
                _attachedFiles.push({
                    ...file,
                    url: signedUrl?.url,
                    urlForUploading: signedUrl?.urlForUploading
                })
            })

            await Promise.all(_attachedFiles.map((item) => uploadUsingPreSignedUrl(item.urlForUploading, item.file)))
        }

        if (mediaLibraryItems.length > 0) {
            mediaLibraryItems.forEach((item) => {
                let url
                switch (item.type) {
                    case "video":
                        url = item.details.videoUrl
                        break
                    default:
                        url = item.details.url
                        break
                }

                _attachedFiles.push({
                    url,
                    filename: item.title,
                    type: item.type,
                    mimetype: item.type,
                    fileSize: 0
                })
            })
            mediaLibraryIds = mediaLibraryItems.map((item) => item.itemId)
        }

        return {
            _attachedFiles: _attachedFiles.map((item) => {
                const {file, poster, fileId, urlForUploading, fileExtension, ...rest} = item
                return rest
            }),
            mediaLibraryIds
        }
    }

    const onSendMessage = async (_msgText: string, _serialize: boolean = undefined) => {
        try {
            setIsSending(true)
            let payload: any = {
                roomId: forThread ? forThread.messageId : activeRoom.roomId,
                content: _msgText,
                type: "message",
                withSms: sendWithSms,
                withEmail: sendWithEmail,
                jsonData: {
                    withStaffsNotification,
                    withActivity: canCreateActivity && withActivity,
                    serialize: _serialize
                }
            }

            const {_attachedFiles, mediaLibraryIds} = await buildAttachedFilesPayload()
            if (mediaLibraryIds.length > 0) {
                payload.jsonData = {...payload.jsonData, mediaLibraryIds}
            }
            if (_attachedFiles.length > 0) {
                payload.files = _attachedFiles
            }

            if (isEditing && editMessage) {
                payload.messageId = editMessage.messageId
            }

            if (replyMessage) {
                payload.replyToMessageId = replyMessage.messageId
            }

            if (sendWithEmail) {
                payload.jsonData = {
                    email: {
                        subject: `${model.clientSetting?.slug.toUpperCase()}: ${subject.trim()}`,
                        html: _msgText,
                        editorType:
                            selectedTemplate?.editorType === Communication.EditorType.Html
                                ? Communication.EditorType.Html
                                : Communication.EditorType.Text
                    }
                }
            }

            ChatWsV2.sendPacket({command: "newMessage", payload})
            resetForm()
            onSuccess?.()
            moveRoomToFirst(Communication.RoomGroupId.Direct, activeRoom.roomId as string)
        } catch (e: any) {
            handleErrorChat(e)
        } finally {
            setIsSending(false)
        }
    }

    const onSubmitThread = async (_msgText: string) => {
        try {
            setIsSending(true)
            let payload: any = {
                description: _msgText,
                name: msgTitle,
                withEmail: sendWithEmail,
                withSms: sendWithSms,
                jsonData: {}
            }

            const {_attachedFiles, mediaLibraryIds} = await buildAttachedFilesPayload()
            if (mediaLibraryIds.length > 0) {
                payload.jsonData = {...payload.jsonData, mediaLibraryIds}
            }
            if (_attachedFiles.length > 0) {
                payload.files = _attachedFiles
            }

            if (isScheduledRoom) {
                payload.scheduledAt = `${moment(scheduledDate).format("YYYY-MM-DD")} ${moment(scheduledTime).format(
                    "HH:mm:ss"
                )}`
                payload.timezone = momentTz.tz.guess()
            }

            let updatedMessage: Communication.Message
            if (isEditing && editMessage) {
                payload.roomId = editMessage.messageId
                updatedMessage = await chatServiceV2.editThread(payload)
            } else {
                payload.parentRoomId = isScheduledRoom ? scheduledChannel.roomId : activeRoom.roomId
                await chatServiceV2.createThread(payload)
            }
            resetForm()
            onSuccess?.(updatedMessage)
        } catch (e: any) {
            handleErrorChat(e)
        } finally {
            setIsSending(false)
        }
    }

    const onSendDirectMessage = async (_msgText: string, _serialize: boolean = undefined) => {
        try {
            setIsSending(true)
            let payload: any = {
                filter: {
                    userIds: (activeRoom as Communication.DirectMessageRoom).directMessageUserIds
                },
                message: _msgText,
                options: {},
                targets: ["chatDirectMessage"]
            }

            let chatDirectMessage: any = {
                content: _msgText,
                senderId: model.user.id,
                type: "message",
                jsonData: {
                    serialize: _serialize,
                    source: {}
                }
            }

            const {_attachedFiles, mediaLibraryIds} = await buildAttachedFilesPayload()
            if (mediaLibraryIds.length > 0) {
                chatDirectMessage.jsonData = {
                    ...chatDirectMessage.jsonData,
                    mediaLibraryIds
                }
            }
            if (_attachedFiles.length > 0) {
                chatDirectMessage.files = _attachedFiles
            }

            if (sendWithSms) {
                chatDirectMessage.withSms = true
                chatDirectMessage.jsonData = {
                    ...chatDirectMessage.jsonData,
                    serialize: true
                }
            }

            if (sendWithEmail) {
                chatDirectMessage.withEmail = true
                chatDirectMessage.jsonData = {
                    ...chatDirectMessage.jsonData,
                    serialize: true,
                    email: {
                        subject: `${model.clientSetting?.slug.toUpperCase()}: ${subject.trim()}`,
                        html: _msgText,
                        editorType:
                            selectedTemplate?.editorType === Communication.EditorType.Html
                                ? Communication.EditorType.Html
                                : Communication.EditorType.Text
                    }
                }
            }
            payload.options = {chatDirectMessage}

            await notificationService.send(payload)
            resetForm()
            onSuccess?.()
        } catch (e: any) {
            handleErrorChat(e)
        } finally {
            setIsSending(false)
        }
    }

    const onSendMultiDepartmentalMessage = async (_msgText: string) => {
        try {
            setIsSending(true)
            let payload: any = {
                roomIds: (activeRoom as Communication.MultiDepartmentalMessageRoom).departmentCampusRoomIds,
                content: _msgText,
                type: "message",
                withEmail: sendWithEmail,
                withSms: sendWithSms,
                jsonData: {
                    withStaffsNotification,
                    withActivity: canCreateActivity && withActivity,
                    serialize: true
                }
            }

            const {_attachedFiles, mediaLibraryIds} = await buildAttachedFilesPayload()
            if (mediaLibraryIds.length > 0) {
                payload.jsonData = {
                    ...payload.jsonData,
                    mediaLibraryIds
                }
            }
            if (_attachedFiles.length > 0) {
                payload.files = _attachedFiles
            }

            if (sendWithEmail) {
                payload.jsonData = {
                    ...payload.jsonData,
                    email: {
                        subject: `${model.clientSetting?.slug.toUpperCase()}: ${subject.trim()}`,
                        html: _msgText,
                        editorType:
                            selectedTemplate?.editorType === Communication.EditorType.Html
                                ? Communication.EditorType.Html
                                : Communication.EditorType.Text
                    }
                }
            }

            await chatServiceV2.sendMessageToMultipleRooms(payload)
            onSuccess?.()
        } catch (e: any) {
            handleErrorChat(e)
        } finally {
            setIsSending(false)
        }
    }

    const onSubmitStudentNote = async (_msgText: string) => {
        try {
            setIsSending(true)
            let payload: any = {
                roomId: activeRoom.roomId,
                content: _msgText,
                type: "message"
            }

            await chatServiceV2.addStudentTopicMessage(payload)
            resetForm()
            onSuccess?.()
        } catch (e: any) {
            handleErrorChat(e)
        } finally {
            setIsSending(false)
        }
    }

    const onSubmitSupport = async () => {
        try {
            setIsSending(true)
            let payload: any = {
                description: msgText,
                name: msgTitle
            }

            const {_attachedFiles} = await buildAttachedFilesPayload()
            if (_attachedFiles.length > 0) {
                payload.files = _attachedFiles
            }

            const room = await chatServiceV2.createSupport(payload)
            resetForm()
            onSuccess?.(room)
        } catch (e: any) {
            handleErrorChat(e)
        } finally {
            setIsSending(false)
        }
    }

    const onSendAndCreateDepartmentalMessage = async (_msgText: string, _serialize: boolean = undefined) => {
        try {
            setIsSending(true)
            const {
                departmentId,
                campusId,
                user: {userId}
            } = activeRoom as Communication.DepartmentRoom
            let payload: any = {
                content: _msgText,
                type: "message",
                withSms: sendWithSms,
                withEmail: sendWithEmail,
                jsonData: {
                    withStaffsNotification,
                    withActivity: canCreateActivity && withActivity,
                    serialize: _serialize
                },
                departmentId,
                campusId,
                userId
            }

            const {_attachedFiles, mediaLibraryIds} = await buildAttachedFilesPayload()
            if (mediaLibraryIds.length > 0) {
                payload.jsonData = {...payload.jsonData, mediaLibraryIds}
            }
            if (_attachedFiles.length > 0) {
                payload.files = _attachedFiles
            }

            if (sendWithEmail) {
                payload.jsonData = {
                    email: {
                        subject: `${model.clientSetting?.slug.toUpperCase()}: ${subject.trim()}`,
                        html: _msgText,
                        editorType:
                            selectedTemplate?.editorType === Communication.EditorType.Html
                                ? Communication.EditorType.Html
                                : Communication.EditorType.Text
                    }
                }
            }

            await chatServiceV2.createDepartmentAndSendMessage(payload)
            resetForm()
            onSuccess?.()
        } catch (e: any) {
            handleErrorChat(e)
        } finally {
            setIsSending(false)
        }
    }

    const onChangeText = (event) => {
        setMsgText(event.target.value)
    }

    const cancelReply = () => {
        setReplyMessage(undefined)
    }

    const onSelectFiles = (e) => {
        const files: File[] = Array.from(e.target.files)
        if (files.length > 0) {
            const newFiles = files.map((file) => ({
                fileId: uuidv4(),
                url: URL.createObjectURL(file),
                mimetype: file.type,
                filename: file.name,
                fileSize: file.size,
                file,
                type: ["image", "video"].includes(file.type.split("/")[0])
                    ? (file.type.split("/")[0] as Communication.AttachedFileType)
                    : Communication.AttachedFileType.File
            }))
            setAttachedFiles((prev) => [...prev, ...newFiles])
        }
    }

    const onClickFile = (e) => {
        e.target.value = ""
    }

    const onSelectEmoji = (emoji) => {
        setMsgText((pre) => `${pre || ""}${emoji.native}`)
    }

    const onSelectMediaLibraryItem = (items: any[]) => {
        setMediaLibraryItems((prev) => [...prev, ...items])
    }

    const onSendBySmsTemplate = async (_msgText: string) => {
        if (activeRoom.roomId === Communication.PredefinedRoomId.WithoutDepartmentalRoom) {
            onSendAndCreateDepartmentalMessage(_msgText, true)
            return
        }
        if (activeRoom.roomId === Communication.PredefinedRoomId.DirectMessage) {
            await onSendDirectMessage(_msgText, true)
            return
        }
        if (activeRoom.type === Communication.RoomTypes.MultiDepartmental) {
            await onSendMultiDepartmentalMessage(_msgText)
            return
        }
        await onSendMessage(_msgText, true)
    }

    const onClickRemoveMediaLibraryItem = (itemId: number) => {
        setMediaLibraryItems((prev) => prev.filter((item) => item.itemId !== itemId))
    }

    const onClickRemoveAttachedFile = (fileIndex: number) => {
        setAttachedFiles((prev) => prev.filter((file, index) => index !== fileIndex))
    }

    const renderChannelView = () => {
        if (onlyContent) {
            if (isEditing && editMessage) {
                return (
                    <div className={styles.actionWrap}>
                        <div className={styles.optionWrap}>
                            <EditIcon className={styles.headingIcon} />
                            <span className={styles.headingTitle}>Edit {forThread ? "comment" : "message"}</span>
                        </div>
                        <div className={styles.option} onClick={resetForm}>
                            <CloseIcon className={styles.headingIcon} />
                        </div>
                    </div>
                )
            }
            return null
        }
        if (![Communication.RoomTypes.Channel, Communication.RoomTypes.Scheduled].includes(activeRoom.type)) {
            return null
        }
        const title = `${isEditing ? "Edit" : "New"} ${isScheduledRoom ? "scheduled" : ""} message`

        return (
            <>
                <div className={styles.actionWrap}>
                    <div className={styles.optionWrap}>
                        <EditIcon className={styles.headingIcon} />
                        <span className={styles.headingTitle}>{title}</span>
                    </div>
                    {isEditing && (
                        <div
                            className={styles.option}
                            onClick={() => {
                                resetForm()
                                if (isEditing) onCancelEdit?.()
                            }}>
                            <CloseIcon className={styles.headingIcon} />
                        </div>
                    )}
                </div>
                {isScheduledRoom && (
                    <div className={styles.channelInputWrap}>
                        <ChannelSelect
                            placeholder="Select channel to post into"
                            value={scheduledChannel}
                            onChange={setScheduledChannel}
                            readOnly={isEditing}
                        />
                    </div>
                )}
                <div className={styles.titleInputWrap}>
                    <input
                        className={styles.titleInput}
                        placeholder="Title"
                        onChange={(e) => setMsgTitle(e.target.value)}
                        value={msgTitle}
                    />
                </div>
            </>
        )
    }

    const renderNewSupportView = () => {
        return (
            <>
                <div className={styles.actionWrap}>
                    <div className={styles.optionWrap}>
                        <EditIcon className={styles.headingIcon} />
                        <span className={styles.headingTitle}>New Support Request</span>
                    </div>
                    <div className={styles.option} onClick={resetForm}>
                        <CloseIcon className={styles.headingIcon} />
                    </div>
                </div>
                <div className={styles.titleInputWrap}>
                    <input
                        className={styles.titleInput}
                        placeholder="Title"
                        onChange={(e) => setMsgTitle(e.target.value)}
                        value={msgTitle}
                    />
                </div>
            </>
        )
    }

    const renderAttachment = () => {
        return (
            <div className={styles.attachmentsWrap}>
                {attachedFiles.map((attachedFile, index) => {
                    const isImage = attachedFile.mimetype.includes("image")
                    const isVideo = attachedFile.mimetype.includes("video")
                    const isFile = !isImage && !isVideo
                    return (
                        <div key={index} className={styles.attachmentContainer}>
                            {isImage && <img src={attachedFile.url} className={styles.previewMedia} alt="" />}
                            {isVideo && (
                                <video autoPlay={false} className={styles.previewMedia} src={attachedFile.url} />
                            )}
                            {isFile && (
                                <>
                                    <FileIcon className={styles.fileIcon} width={24} height={24} />
                                    <div className={styles.attachmentFileName}>{attachedFile.filename}</div>
                                </>
                            )}
                            <Tooltip title="Remove attachment">
                                <CloseIcon
                                    className={isFile ? styles.removeIcon : styles.removePreview}
                                    width={24}
                                    height={24}
                                    onClick={() => onClickRemoveAttachedFile(index)}
                                />
                            </Tooltip>
                        </div>
                    )
                })}
                {mediaLibraryItems.map((item, index) => (
                    <div key={index} className={styles.attachmentContainer}>
                        <Icon icon={MEDIA_LIBRARY_ICONS[item.type]} className={styles.mediaLibraryIcon} />
                        <div className={styles.attachmentFileName}>{item.title}</div>
                        <Tooltip title="Remove media library">
                            <CloseIcon
                                className={styles.removeIcon}
                                width={24}
                                height={24}
                                onClick={() => onClickRemoveMediaLibraryItem(item.itemId)}
                            />
                        </Tooltip>
                    </div>
                ))}
            </div>
        )
    }

    const renderReplyMessage = () => {
        if (replyMessage && replyMessage.messageId) {
            return (
                <div className={styles.replyWrap}>
                    <ReplyMessage message={replyMessage} containerStyle={styles.replyContainer} />
                    <div className={cx(styles.option, styles.cancelReply)} onClick={cancelReply}>
                        <CloseIcon className={styles.cancelReplyIcon} />
                    </div>
                </div>
            )
        }
    }

    const availableMentionUsers = useMemo(() => {
        return (userList || [])
            .filter((x) => !x.isMe)
            .map((x) => ({
                id: x.userId,
                display: x.fullName,
                description: "",
                image: x.photo,
                photo: x.photo,
                fullName: x.fullName
            }))
    }, [userList])

    const submitText = isEditing ? "Save" : isChannelOrScheduledRoom && !forThread ? "Create" : "Send"

    const renderMessageOptions = () => {
        if (activeRoom.type === Communication.RoomTypes.StudentTopic) {
            return null
        }
        const htmlOptions = []
        if (!sendWithEmail && !sendWithSms)
            htmlOptions.push(
                <Tooltip title="Add emoji">
                    <div className={styles.option} onClick={emojiPopup.open}>
                        <EmojiIcon className={styles.icon} />
                        <EmojiPicker
                            isShow={emojiPopup.isVisible}
                            onClose={emojiPopup.close}
                            onSelect={onSelectEmoji}
                            className={cx(styles.emojiPicker, emojiPickerClassName)}
                        />
                    </div>
                </Tooltip>
            )

        if (!isEditing) {
            if (isPollEnabled) {
                htmlOptions.push(
                    <Tooltip title="Create a Poll">
                        <div className={styles.option} onClick={pollPopup.open}>
                            <Icon icon="BAR_CHART" color="#1E90FF" className={styles.icon} />
                        </div>
                    </Tooltip>
                )
            }
            htmlOptions.push(
                <Tooltip title="Send a file">
                    <div className={styles.option} onClick={() => attachFileRef.current.click()}>
                        <AttachmentIcon className={styles.icon} />
                        <input
                            style={{display: "none"}}
                            id="send-file"
                            ref={attachFileRef}
                            type="file"
                            multiple
                            onChange={onSelectFiles}
                            onClick={onClickFile}
                            accept="image/*,video/*,.pdf"
                        />
                    </div>
                </Tooltip>
            )
            if (!isNewSupportRequest && !isSupportRoom) {
                htmlOptions.push(
                    <Tooltip title="Attach from media library">
                        <div className={styles.option} onClick={libraryPopup.open}>
                            <FolderIcon className={styles.icon} />
                        </div>
                    </Tooltip>
                )
            }
            if (showSendOptions && !sendWithEmail && !sendWithSms) {
                htmlOptions.push(
                    <Tooltip title="Send from Text Message templates">
                        <div className={styles.option} onClick={messageTemplatePopup.open}>
                            <Icon icon="SMS" className={styles.icon} />
                        </div>
                    </Tooltip>
                )
            }
        }

        const options = htmlOptions.map((option, index) => {
            return (
                <Fragment key={index}>
                    {index > 0 && <div className={styles.divider} />}
                    {option}
                </Fragment>
            )
        })

        return options
    }

    return (
        <div className={styles.root}>
            {renderChannelView()}
            {isNewSupportRequest && renderNewSupportView()}
            {renderReplyMessage()}
            <TemplateSelection
                withEmail={sendWithEmail}
                withSMS={sendWithSms}
                setValue={setMsgText}
                selectedTemplate={selectedTemplate}
                setSelectedTemplate={setSelectedTemplate}
                descendants={descendants}
                setDescendants={setDescendants}
                setSubject={setSubject}
                subject={subject}
                selectedUserId={selectedUserId}
                serializeWithoutUser={serializeWithoutUser}
            />
            {!sendWithEmail && !sendWithSms ? (
                <>
                    <MentionsInput
                        placeholder={
                            isNewSupportRequest || isChannelOrScheduledRoom ? "Type your reply..." : "Type message"
                        }
                        value={msgText}
                        onKeyDown={onPressEnterSend}
                        onChange={onChangeText}
                        className={"mentions"}
                        classNames={inputStyles}>
                        <Mention
                            trigger="@"
                            markup="[@__display__:__id__]"
                            displayTransform={(_, display) => {
                                return `@${display}`
                            }}
                            style={{
                                backgroundColor: "#cee4e5"
                            }}
                            data={availableMentionUsers}
                            renderSuggestion={(suggestion, search, highlightedDisplay, index, focused) => {
                                return (
                                    <div className={styles.mentionsListItem}>
                                        <div className={styles.mentionsListImageWrapper}>
                                            {["0", "00"].includes(suggestion.userId) ? (
                                                <Icon icon="VOL" />
                                            ) : (
                                                <UserAvatar user={suggestion} />
                                            )}
                                        </div>
                                        {highlightedDisplay}{" "}
                                        <em className={styles.descriptionMention}>{suggestion.fullName}</em>
                                    </div>
                                )
                            }}
                        />
                    </MentionsInput>
                </>
            ) : (
                <RichTextEditor
                    value={descendants}
                    onChange={setDescendants}
                    userDateFormat={userDateFormat}
                    userTimeFormat={userTimeFormat}
                    dataFieldInsertable
                    inputFieldInsertable={false}
                    wrapperClassName={styles.messageRichTextEditor}
                />
            )}
            {renderAttachment()}
            <div className={styles.actionWrap}>
                <div className={styles.optionWrap}>{renderMessageOptions()}</div>
                <div className={styles.actionRightWrap}>
                    {isScheduledRoom && (
                        <div className={styles.scheduledSettingWrap}>
                            <span className={styles.scheduledText}>Post on</span>
                            <BaseDatePicker
                                placeholder="Date"
                                disabledDate={(currentDate) => currentDate < moment().startOf("day")}
                                value={scheduledDate}
                                onChange={setScheduledDate}
                            />
                            <BaseTimePicker
                                placeholder="Time"
                                format="HH:mm"
                                value={scheduledTime}
                                onChange={setScheduledTime}
                            />
                        </div>
                    )}
                    <BaseButton
                        type="primary"
                        title={submitText}
                        size="small"
                        disabled={!isSendAvailable}
                        loading={isSending}
                        onClick={onClickSend}
                    />
                </div>
            </div>
            {showSendOptions && !isEditing && (
                <div className={styles.actionWrap}>
                    <div className={styles.optionWrap}>
                        <div
                            className={cx(styles.checkWrap, {[styles.checkedWrap]: sendWithEmail})}
                            onClick={onToggleSendAsEmail}>
                            <Checkbox checked={sendWithEmail} />
                            <span className={styles.checkTitle}>Send As Email</span>
                        </div>
                        {isDepartmentRoom && isStaffOrAdmin && (
                            <div
                                className={cx(styles.checkWrap, {[styles.checkedWrap]: withStaffsNotification})}
                                onClick={() => setWithStaffsNotification(!withStaffsNotification)}>
                                <Checkbox checked={withStaffsNotification} />
                                <span className={styles.checkTitle}>Include notification to rest of staffs</span>
                            </div>
                        )}
                        {canSendSms && (
                            <div
                                className={cx(styles.checkWrap, {[styles.checkedWrap]: sendWithSms})}
                                onClick={onToggleSendAsSms}>
                                <Checkbox checked={sendWithSms} />
                                <span className={styles.checkTitle}>Send As SMS</span>
                            </div>
                        )}
                        {canCreateActivity && (
                            <div
                                className={cx(styles.checkWrap, {[styles.checkedWrap]: withActivity})}
                                onClick={() => setWithActivity(!withActivity)}>
                                <Checkbox checked={withActivity} />
                                <span className={styles.checkTitle}>Save to activity</span>
                            </div>
                        )}
                    </div>
                </div>
            )}
            <PollPopupV2 isShow={pollPopup.isVisible} onClosePopup={pollPopup.close} roomId={pollRoomId} />
            <MediaLibraryPopup
                isShow={libraryPopup.isVisible}
                onClose={libraryPopup.close}
                onSelect={onSelectMediaLibraryItem}
            />
            {showSendOptions && (
                <MessageTemplatePopup
                    selectedUserId={selectedUserId}
                    isShow={messageTemplatePopup.isVisible}
                    onClose={messageTemplatePopup.close}
                    onSelect={onSendBySmsTemplate}
                    serializeWithoutUser={serializeWithoutUser}
                />
            )}
            {isSending && <BaseLoading isShow={isSending} />}
        </div>
    )
})
